import React, { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import AgTable from "../AgTable";
import { APPINSIGHTS_STRINGS } from "../../constants/appInsightsStrings";
import { trackEvent } from "../../utils/appInsights";
import { useTranslation } from "react-i18next";

import { deleteCip, getCIPs } from "../../network/cipAPI";
import { storeCIPS } from "../../Store/TemplateStore/template.action";

import AddCIPDialog from "../../pages/addCIP";
import DupontDialog from "../DupontDialog";
import CriticalDeleteDialog from "../CriticalDeleteDialog";
import ConfirmationPopup, {
  DIALOG_TYPES,
} from "../../packages/ConfirmationPopup";
import {
  ADD_CIP_MENU_IDS,
  CIP_VALUES,
  CustomModalButtons,
  STORAGE_KEYS,
} from "../../constants/global.constants";
import { getSortedData } from "../../utils/tableDataFormatter";
import createNotification, {
  ERROR_NOTIFICATION,
  SUCCESS_NOTIFICATION,
} from "../Notifications";
import { USER_ADDED_TABLE_COLUMNS } from "./userAddedCipTable.config";
import PopoverText from "../PopoverText";
import InfoIconGreen from "../../assets/info-icon-green.svg";
import { WPDropDownMenu } from "../../packages/WPDropDownMenu/WPDropDownMenu";
import AccordionDownIcon from "../../packages/SVGs/AccordionDownIcon";
import BulkUploadDialog from "./BulkUploadDialog";
import SkipDialog from "./SkipDialog";

import "./index.scss";

const UserAddedCIP = ({
  fileInfo,
  min_date,
  max_date,
  fileId,
  setView,
  addCecButtonDisable,
}) => {
  const { t, i18n } = useTranslation();
  const gridRef = useRef(null);
  const dispatch = useDispatch();

  const [gridApi, setGridAPi] = useState(null);
  const [addDialogOpen, setAddDilogOpen] = useState(false);
  const [isEdited, setIsEdited] = useState(false);
  const [editedRow, setEditedRow] = useState(null);
  const userCips = useSelector((state) => state.cips);
  const selectedTrain = useSelector((state) => state.sel_train);
  const temperature = useSelector((state) => state.temperature);
  const mass = useSelector((state) => state.mass);
  const [rowData, setRowData] = useState(
    userCips
      ? getSortedData(userCips.filter((item) => !item.is_tool_identified))
      : []
  );
  const [deletedRow, setDeletedRow] = useState(null);
  const [showDeletePopup, setShowDeletedDialog] = useState(false);
  const [updateLanguage, setUpdateLanguage] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [cipSource, setCipSource] = useState(fileInfo?.cip_source || "");
  const [showSourceConflictDialog, setShowSourceConflictDialog] =
    useState(false);
  const [pendingAddType, setPendingAddType] = useState("");
  const [showIntroDialog, setShowIntroDialog] = useState(false);
  const [pendingIntroAddType, setPendingIntroAddType] = useState("");
  const [neverShowIntroAgain, setNeverShowIntroAgain] = useState(false);

  useEffect(() => {
    setUpdateLanguage((prev) => !prev);
  }, [i18n.language]);

  useEffect(() => {
    if (!fileId) {
      setRowData([]);
    }
  }, [fileId]);

  useEffect(() => {
    setCipSource(fileInfo?.cip_source || "");
  }, [fileInfo]);

  useEffect(() => {
    try {
      const persisted = localStorage.getItem(STORAGE_KEYS.CIP_NEVER_SHOW_AGAIN);
      setNeverShowIntroAgain(persisted === "true");
    } catch (err) {
      setNeverShowIntroAgain(false);
    }
  }, []);

  useEffect(() => {
    const fetchCips = async () => {
      try {
        const cips = await getCIPs();
        if (cips && cips?.data) {
          const userCips = cips?.data.filter(
            (item) => !item.is_tool_identified
          );
          setRowData(getSortedData(userCips));
        }
      } catch (err) { }
    };
    fetchCips();
  }, []);

  const deleteHandler = async (params) => {
    setDeletedRow(params);
    setShowDeletedDialog(true);
  };

  const editHandler = (params) => {
    setIsEdited(true);
    setAddDilogOpen(true);
    setEditedRow(params);
  };

  const [columnDefs, setColumnDefs] = useState(
    USER_ADDED_TABLE_COLUMNS(t, temperature, mass)
  );

  useEffect(() => {
    setColumnDefs(USER_ADDED_TABLE_COLUMNS(t, temperature, mass));
  }, [t, setColumnDefs]);

  const onGridReady = useCallback((params) => {
    let gridApi = params.api;
    setGridAPi(gridApi);
  });

  const onAddCIPClose = () => {
    setAddDilogOpen(false);
    setIsEdited(false);
    setEditedRow(null);
  };

  const openAddDialogByType = (addType) => {
    if (addType === CIP_VALUES.USER) {
      setAddDilogOpen(true);
      return;
    }
    setBulkDialogOpen(true);
  };

  const openIntroOrAddDialogByType = (addType) => {
    if (!neverShowIntroAgain) {
      setPendingIntroAddType(addType);
      setShowIntroDialog(true);
      return;
    }
    openAddDialogByType(addType);
  };

  const hasSourceConflict = (addType, source) => {
    if (!source) return false;
    return (
      (addType === CIP_VALUES.BULK && source === CIP_VALUES.USER) ||
      (addType === CIP_VALUES.USER && source === CIP_VALUES.BULK)
    );
  };

  const handleAddActionClick = (addType) => {
    if (hasSourceConflict(addType, cipSource)) {
      setPendingAddType(addType);
      setShowSourceConflictDialog(true);
      return;
    }

    openIntroOrAddDialogByType(addType);
  };

  const onAddClick = () => {
    handleAddActionClick(CIP_VALUES.USER);
  };

  const onAddBulkClick = () => {
    handleAddActionClick(CIP_VALUES.BULK);
  };

  const onBulkDialogClose = () => {
    setBulkDialogOpen(false);
  };

  const onBulkUploadSuccess = (filteredCips, allCips) => {
    setRowData(getSortedData(filteredCips || []));
    updateStoreCips(allCips || filteredCips || []);
    setCipSource(CIP_VALUES.BULK);
  };

  const onSingleCipSaved = () => {
    setCipSource(CIP_VALUES.USER);
  };

  const onSourceConflictDialogClose = (action) => {
    setShowSourceConflictDialog(false);

    if (action === CustomModalButtons.CONFIRM && pendingAddType) {
      openIntroOrAddDialogByType(pendingAddType);
    }

    setPendingAddType("");
  };

  const onIntroDialogClose = (action) => {
    setShowIntroDialog(false);

    const actionId = action?.target?.id || action;

    if (actionId === CustomModalButtons.CONFIRM && pendingIntroAddType) {
      openAddDialogByType(pendingIntroAddType);
    }

    setPendingIntroAddType("");
  };

  const onNeverShowAgainChange = (checked) => {
    setNeverShowIntroAgain(checked);
    try {
      localStorage.setItem(STORAGE_KEYS.CIP_NEVER_SHOW_AGAIN, String(checked));
    } catch (err) {
      // Ignore storage write failures and keep in-memory preference.
    }
  };

  const addMenuItems = [
    {
      id: ADD_CIP_MENU_IDS.ADD_CIP,
      label: t("CIPShutdownStrings.addCip"),
      isDisabled: addCecButtonDisable,
      onClick: onAddClick,
    },
    {
      id: ADD_CIP_MENU_IDS.ADD_BULK,
      label: t("CIPShutdownStrings.addBulk"),
      isDisabled: addCecButtonDisable,
      onClick: onAddBulkClick,
    },
  ];

  const onProceedClicked = async (value) => {
    setShowDeletedDialog(false);
    if (value === CustomModalButtons.CONFIRM) {
      const deleteCips = await deleteCip(deletedRow.cip_id);
      if (deleteCips && deleteCips.status === 200) {
        // Track CIP deletion
        trackEvent(APPINSIGHTS_STRINGS.CIP_DELETED, {
          cipId: deletedRow.cip_id,
          type: deletedRow.type,
          startDate: deletedRow.start_date,
          endDate: deletedRow.end_date
        });

        createNotification(
          SUCCESS_NOTIFICATION,
          t("notificationStrings.deleteSuccess"),
          ""
        );
        setView(2);
        const cips = await getCIPs();
        if (cips.status === 200 && cips) {
          const filteredCips = cips?.data
            ? cips?.data.filter((item) => !item.is_tool_identified)
            : [];
          setRowData(filteredCips);
        }
      } else {
        createNotification(
          ERROR_NOTIFICATION,
          t("notificationStrings.deleteFail"),
          ""
        );
      }
    }
  };
  const updateStoreCips = (data) => {
    dispatch(storeCIPS([...data]));
  };

  return (
    <div>
      <div
        className="lightGrayBorder"
        style={{ padding: "16px", marginTop: "10px" }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div className="headerTealText">{t("CIPShutdownStrings.title")}</div>
            <PopoverText
              descriptor={<img src={InfoIconGreen} alt="icon" />}
              descriptionText={t("CIPShutdownStrings.recordPastCIPsAndShutdowns")}
            />
          </div>
          <WPDropDownMenu
            id="add-cip-shutdown-menu"
            toggle={
              <div className="predict-download-toggle">
                {t("CIPShutdownStrings.add")}
                <AccordionDownIcon />
              </div>
            }
            disabled={addCecButtonDisable}
            customClass="predict-dropdown-tertiary"
            menuItems={addMenuItems}
          />
          {addDialogOpen && (
            <AddCIPDialog
              open={addDialogOpen}
              onClose={onAddCIPClose}
              rowData={rowData}
              setRowData={setRowData}
              isEdited={isEdited}
              editedRow={editedRow}
              gridAPI={gridApi}
              fileInfo={fileInfo}
              max_date={max_date}
              min_date={min_date}
              setOpen={setAddDilogOpen}
              updateStoreCips={updateStoreCips}
              isUSerAdded
              setView={setView}
              onSaved={onSingleCipSaved}
            />
          )}
          {bulkDialogOpen && (
            <BulkUploadDialog
              open={bulkDialogOpen}
              onClose={onBulkDialogClose}
              onUploaded={onBulkUploadSuccess}
              setView={setView}
              selectedTrain={selectedTrain}
            />
          )}
          {showSourceConflictDialog && (
            <ConfirmationPopup
              open={showSourceConflictDialog}
              type={DIALOG_TYPES.ERROR}
              header={t("CIPShutdownStrings.sourceConflictTitle")}
              description={
                pendingAddType === CIP_VALUES.BULK
                  ? t("CIPShutdownStrings.sourceConflictBodyBulk")
                  : t("CIPShutdownStrings.sourceConflictBodySingle")
              }
              cancelBtn={t("ValueStrings.cancel")}
              confirmBtn={
                pendingAddType === CIP_VALUES.BULK
                  ? t("CIPShutdownStrings.proceedBulkEvent")
                  : t("CIPShutdownStrings.proceedSingleEvent")
              }
              onClose={onSourceConflictDialogClose}
            />
          )}
          {showIntroDialog && (
            <SkipDialog
              onClose={onIntroDialogClose}
              CIPNeverShowAgain={neverShowIntroAgain}
              onNeverShowAgainChange={onNeverShowAgainChange}
            />
          )}
        </div>
        <div
          className="ag-theme-alpine"
          style={{ width: "100%", marginTop: "10px", overflow: "auto" }}
        >
          <AgTable
            key={updateLanguage}
            ref={gridRef} 
            columnDefs={columnDefs}
            rowData={rowData}
            onGridReady={onGridReady}
            context={{ deleteHandler, editHandler }}
            name={t("excelSheetNames.cipAndShutdown")}
          />
          {showDeletePopup && (
            <DupontDialog
              open={showDeletePopup}
              type="primary"
              title={t("notificationStrings.deleteConfirmationCIP")}
              cancelBtnText={t("ValueStrings.no")}
              confirmBtnText={t("ValueStrings.yes")}
              onClose={onProceedClicked}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default UserAddedCIP;
